import { connectorCapabilitiesAreActionable } from '@frontend/features/rutero/connectorCapabilitiesQueryKey';

import type { ConnectorCapabilities, ConnectorInfo } from './queries';

export interface ConnectorCapabilityIdentity {
  provider?: string;
  vertical: string;
}

export type ConnectorCapabilityResolution =
  | {
      resolved: true;
      capabilities: ConnectorCapabilities | undefined;
    }
  | {
      resolved: false;
      capabilities: undefined;
    };

/**
 * `servedBy` de `/places/autocomplete` para UN vertical: los ids de
 * CONECTOR (no `provider`) configurados para esta agencia en ese vertical.
 * Nunca hardcodear un vertical ni un id de conector en el frontend — sale
 * SIEMPRE de `GET /rutero/connectors` (21.1 aplicado a datos, no solo a
 * auth: el frontend no decide qué conectores existen).
 */
export function deriveServedByForVertical(
  connectors: readonly ConnectorInfo[] | undefined,
  vertical: string,
): string[] {
  if (!connectors) return [];
  return connectors
    .filter(
      (connector) =>
        connector.vertical === vertical && connector.configured && connector.availableCapabilities?.search === true,
    )
    .map((connector) => connector.connector);
}

export function resolveConnectorCapabilities(
  connectors: readonly ConnectorInfo[] | undefined,
  { provider, vertical }: ConnectorCapabilityIdentity,
): ConnectorCapabilityResolution {
  if (!provider || !vertical) return { resolved: false, capabilities: undefined };

  const matches =
    connectors?.filter((connector) => connector.provider === provider && connector.vertical === vertical) ?? [];

  if (matches.length !== 1) return { resolved: false, capabilities: undefined };

  return { resolved: true, capabilities: matches[0].availableCapabilities };
}

/** Diagnóstico contractual; nunca se usa para inferir disponibilidad. */
export function connectorUnavailableReasons(
  connectors: readonly ConnectorInfo[] | undefined,
  { provider, vertical }: ConnectorCapabilityIdentity,
) {
  if (!provider || !vertical) return [];
  const matches =
    connectors?.filter((connector) => connector.provider === provider && connector.vertical === vertical) ?? [];
  return matches.length === 1 ? (matches[0].unavailableReasons ?? []) : [];
}

export const connectorCapabilityAuthorityIsActionable = ({
  resolution,
  isSuccess,
  isFetching,
}: {
  resolution: ConnectorCapabilityResolution;
  isSuccess: boolean;
  isFetching: boolean;
}) => connectorCapabilitiesAreActionable({ isSuccess, isFetching }) && resolution.resolved;

/**
 * Lo que sabemos sobre UNA capacidad del conector de esta reserva. Son tres
 * estados distintos y colapsarlos en un booleano es lo que hacía que se
 * ofreciera "Ver tickets" en proveedores que no los exponen:
 *
 * - `supported`   — el conector dice que sí.
 * - `unsupported` — el conector dice que no; se le puede explicar al agente.
 * - `unknown`     — todavía no sabemos, o no pudimos preguntar.
 */
export type ConnectorCapabilityVerdict = 'supported' | 'unsupported' | 'unknown';

/**
 * Fail-CLOSED: una acción se ofrece SOLO con un `supported` explícito. Ofrecer
 * lo que después falla con un 400 es peor que no ofrecerlo.
 *
 * La decisión usa `availableCapabilities`, no el soporte estático: así un
 * bloqueo externo declarado por Rutero falla cerrado aunque exista código.
 */
export function connectorCapabilityVerdict(
  capability: keyof ConnectorCapabilities,
  {
    resolution,
    isSuccess,
    isFetching,
  }: {
    resolution: ConnectorCapabilityResolution;
    isSuccess: boolean;
    isFetching: boolean;
  },
): ConnectorCapabilityVerdict {
  if (!connectorCapabilityAuthorityIsActionable({ resolution, isSuccess, isFetching })) {
    return 'unknown';
  }
  const supported = resolution.capabilities?.[capability];
  if (supported === true) return 'supported';
  if (supported === false) return 'unsupported';
  return 'unknown';
}
